import { noop } from 'utils/noop';
import debounce from 'lodash/debounce';
import { KeyRoot, KeyQuality, KeyRootAPI, CreatorEvents } from 'types/enums';
import { EventSubscription, Observer } from 'utils/eventObserver';
import { log } from 'utils/log';
import {
    CreatorEventPayloadType,
    CreatorBpmMultiplier,
} from 'controllers/business/Creator/Creator.types';
import { EventIds } from '../../constants';
import {
    CreatorTrackConfigType,
    CreatorTrackMapType,
    CreatorPlaybackEvent,
    CreatorPlaybackAction,
    CreatorMixdownTrackType,
} from './CreatorSession.types';
import {
    getKeyAsSemitone,
    unlockWebAudioContext,
    getDyadicBarsLengthInSeconds,
    getSampleCountFromSeconds,
    downloadMixdown,
} from './CreatorSession.utils';
import { CreatorSessionTrack } from './CreatorSessionTrack';
import { CreatorSessionEventQueue } from './CreatorSessionEventQueue';

// LOUDNESS
const PlaybackTargetAmplitude = 1;
const MixdownGainPeak = 0.891251; // Equivalent to a reduction of -1 dB from 1.0

// SCHEDULING
const SequencerIntervalInMilliseconds = 25;
const DebounceEventMs = 60 * 1000;

// AUDIO ENCODING
// Safari will ignore this suggestion, so always read the `sampleRate` from the
// `AudioContext`.
const SuggestedSampleRate = 44100;
const MixdownMp3BitRate = 320;

// RETRIES
const MaxMixdownRetries = 1;

/**
 * 3 seconds is REALLY long, but performance issues are causing some scheduled
 * events to not be executed for at least that length of time. Needs further
 * investigation.
 * JIRA - https://mixgenius.atlassian.net/browse/SP-3905
 */
const LookaheadInSeconds = 3;

export class CreatorSession {
    // FLAGS
    private isPaused = false; // Has the user stopped playback?
    private _isPlaying = false; // Is the scheduler running?
    public get isPlaying(): boolean {
        return this._isPlaying;
    } // Is the scheduler running?
    private _useLegacyProcessing = false; //
    public isProcessing = false; // Is data processing preventing playback?
    public readonly isInitialized: boolean = false;

    // BASE
    private _baseBpm = 120;
    get baseBpm(): number {
        return this._baseBpm;
    }
    private baseKeyAsSemitone = 0;
    private baseKeyRoot: KeyRoot | KeyRootAPI = KeyRoot.C;
    private baseKeyQuality = KeyQuality.Major;

    // RETRIES
    private mixdownRetries = 0;

    // TRACKS
    private _tracks: CreatorTrackMapType = new Map();
    /**
     * WARNING: Don't manipulate tracks directly
     */
    get tracks(): CreatorTrackMapType {
        return new Map(this._tracks);
    }
    private get _baseTrack(): CreatorSessionTrack {
        return this.getTrackById(this.baseTrackId);
    }
    private get _firstTrack(): CreatorSessionTrack {
        return [...this.tracks.values()][0] || this.createEmptyTrack();
    }
    private get _longestTrack(): CreatorSessionTrack {
        let longestTrack = this._firstTrack;

        this.tracks.forEach((track) => {
            if (track.warpBuffer.duration > longestTrack.scaledDuration) {
                longestTrack = track;
            }
        });

        return longestTrack;
    }
    private baseTrackId = '';
    private sequencerIntervalId = 0;
    private eventQueue = new CreatorSessionEventQueue();
    protected startTime = 0;

    public setTrackUIReady = (id: string) => {
        this.getTrackById(id)?.setUIReady();
        this.handleTrackReady();
    };

    // AUDIO
    public audioContext: AudioContext;
    public masterGainNode: GainNode;

    // MIXDOWN
    public get mixdownDuration(): number {
        const mixdownLengthInSeconds =
            this._longestTrack.getScaledDyadicDurationInSeconds(this._baseBpm);

        return mixdownLengthInSeconds;
    }
    private mixdownWorker: Worker | undefined;
    private resolveMixdown:
        | ((value: Blob | PromiseLike<Blob>) => void)
        | undefined;
    private resolveMixdownWaitingForProcessing: (() => void) | undefined;

    // TIMEOUT
    private timeoutInMs =
        window.navigator.userAgent.includes('Mozilla') &&
        window.navigator.vendor.includes('Apple')
            ? 250
            : 0;

    constructor(useLegacyProcessing = false) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext({
            sampleRate: SuggestedSampleRate,
            latencyHint: 'playback',
        });

        unlockWebAudioContext(audioContext);

        const masterGainNode = audioContext.createGain();

        masterGainNode.connect(audioContext.destination);
        masterGainNode.gain.value = 0;
        this._useLegacyProcessing = useLegacyProcessing;
        this.audioContext = audioContext;
        this.masterGainNode = masterGainNode;
        this.isPaused = true;
        this.isProcessing = false;
        this.isInitialized = true;
    }

    // EVENTS
    private subscriptions: {
        [key: string]: EventSubscription<CreatorEventPayloadType>;
    } = {
        PAUSE_UPDATED: new EventSubscription(CreatorEvents.PAUSE_UPDATED),
        PROCESSING_UPDATED: new EventSubscription(
            CreatorEvents.PROCESSING_UPDATED,
        ),
        TRACKS_UPDATED: new EventSubscription(CreatorEvents.TRACKS_UPDATED),
        PLAYBACK_UPDATED: new EventSubscription(CreatorEvents.PLAYBACK_UPDATED),
        TRACK_STARTED: new EventSubscription(CreatorEvents.TRACK_STARTED),
        TRACK_GAIN_UPDATED: new EventSubscription(
            CreatorEvents.TRACK_GAIN_UPDATED,
        ),
    };

    public subscribe = (
        event: CreatorEvents,
        observer: Observer<CreatorEventPayloadType>,
    ): void => {
        this.subscriptions[event].subscribe(observer);
    };

    public unsubscribe = (
        event: CreatorEvents,
        observer: Observer<CreatorEventPayloadType>,
    ): void => {
        this.subscriptions[event].unsubscribe(observer);
    };

    private broadcast(event: CreatorEvents): void {
        switch (event) {
            case CreatorEvents.PAUSE_UPDATED:
                this.subscriptions[CreatorEvents.PAUSE_UPDATED].immediate(
                    this.isPaused,
                );
                break;

            case CreatorEvents.PROCESSING_UPDATED:
                this.subscriptions[CreatorEvents.PROCESSING_UPDATED].immediate(
                    this.isProcessing,
                );
                break;

            case CreatorEvents.PLAYBACK_UPDATED:
                this.subscriptions[CreatorEvents.PLAYBACK_UPDATED].immediate(
                    this._tracks,
                );
                break;

            case CreatorEvents.TRACKS_UPDATED:
                this.subscriptions[CreatorEvents.TRACKS_UPDATED].debounced(
                    this._tracks,
                );
                break;

            case CreatorEvents.TRACK_GAIN_UPDATED:
                this.subscriptions[CreatorEvents.TRACK_GAIN_UPDATED].debounced(
                    this._tracks,
                );
                break;

            case CreatorEvents.TRACK_STARTED:
                this.subscriptions[CreatorEvents.TRACK_STARTED].immediate(
                    this._tracks,
                );
                break;
        }
    }

    // ACCESSORS

    public get currentTime(): number {
        return this.audioContext.currentTime;
    }

    public hasTrack = (guid: string): boolean => {
        return Boolean(this._tracks.get(guid));
    };

    /**
     * Return an empty track to make `getTrackById` simpler to use. Knowing we,
     * always receive a track - even when the id does not match a tracks in the
     * session, allows us reduce code complexity associated with null checking.
     */
    private createEmptyTrack = (): CreatorSessionTrack => {
        const config = {
            id: '',
            key: null,
            bpm: 120,
            bpmMultiplier: 'None' as CreatorBpmMultiplier,
            duration: 0,
            gain: 1,
            name: '',
            stream: {} as Response,
            pitchTranspose: 0,
            isAutoload: false,
        };

        const emptyTrack = new CreatorSessionTrack({
            id: '',
            config,
            session: this,
            onTrackReady: noop,
            useLegacyProcessing: this._useLegacyProcessing,
        });

        return emptyTrack;
    };

    public getTrackGain = (guid: string): number => {
        return this.getTrackById(guid).calculatedGain;
    };

    private getTrackById = (guid: string): CreatorSessionTrack => {
        const track = this._tracks.get(guid);

        if (!track) {
            return this.createEmptyTrack();
        }

        return track;
    };

    /**
     * TRACK PROGRESS & SCHEDULING
     */

    private resetTracksStartedAt = (): void => {
        this._tracks.forEach(
            (track: CreatorSessionTrack) => track.resetStartedAt,
        );
        this.broadcast(CreatorEvents.PLAYBACK_UPDATED);
    };

    private handleTrackReady = (resetGainOnAllTracks = true): void => {
        let allTracksAreReady = true;
        let allTracksAreUIReady = true;

        if (resetGainOnAllTracks) {
            this.setGainScaleOnAllTracks();
        }

        this._tracks.forEach((track): void => {
            if (!track.isPlaybackReady) {
                allTracksAreReady = false;
            }

            if (!track.isUIReady) {
                allTracksAreUIReady = false;
            }
        });

        if (allTracksAreReady) {
            this.resolveMixdownWaitingForProcessing?.();
        }

        if (allTracksAreReady) {
            this.stopProcessing();
        }

        if (allTracksAreReady && allTracksAreUIReady && !this.isPaused) {
            this.promoteLongestTrack();
            this.startPlaying();
        }
    };

    private rescheduleAllTracks = (): void => {
        this.promoteLongestTrack();

        this.startTrackScheduler();

        this.broadcast(CreatorEvents.PLAYBACK_UPDATED);
    };

    private resetQueue = (): void => {
        this.eventQueue.reset();

        clearInterval(this.sequencerIntervalId);

        if (this._useLegacyProcessing) {
            this._tracks.forEach((track) => {
                try {
                    track.activeBufferNode?.stop(this.audioContext.currentTime);
                } catch {
                    // Safari errors if you call `stop` on a node that's already
                    // stopped, but the API spec says it's fine
                }
                try {
                    track.staleBufferNode?.stop(this.audioContext.currentTime);
                } catch {
                    // See above
                }

                track.activeBufferNode?.disconnect();
                track.staleBufferNode?.disconnect();

                track.activeBufferNode = undefined;
                track.staleBufferNode = undefined;
            });
        } else {
            this._tracks.forEach((track) => {
                track.stopNow();
            });
        }
    };

    private stopTrackScheduler = (): void => {
        this.resetQueue();
    };

    private startTrackScheduler = (): void => {
        this.resetQueue();

        // Hack so that we don't get weird playback after processing on Safari
        setTimeout(() => {
            this.startTime = this.audioContext.currentTime + 0.1;

            this._tracks.forEach((track) => {
                this.startTrack(track, this.startTime);
            });

            (this.sequencerIntervalId as unknown as NodeJS.Timeout) =
                setInterval(() => {
                    this.eventQueue
                        .getEventsInFrame(
                            this.audioContext.currentTime + LookaheadInSeconds,
                        )
                        .forEach(this.handleQueueEvent);
                }, SequencerIntervalInMilliseconds);
        }, 0);
    };

    // Prevent user to overflow the log
    private debouncedCreatorSequencerMissedEvent = debounce(
        (event) => {
            log.warn(
                `Creator sequencer missed an event`,
                EventIds.CreatorSequencerMissedEvent,
                undefined,
                {
                    scheduledTime: event.time,
                    executedTime: this.audioContext.currentTime,
                    timeDelta: this.audioContext.currentTime - event.time,
                },
            );
        },
        DebounceEventMs,
        {
            leading: true,
            trailing: false,
            maxWait: DebounceEventMs,
        },
    );

    private handleQueueEvent = (event: CreatorPlaybackEvent): void => {
        if (event.time < this.audioContext.currentTime) {
            this.debouncedCreatorSequencerMissedEvent(event);
        }

        switch (event.action) {
            case CreatorPlaybackAction.START:
                this.startTrack(this.getTrackById(event.trackId), event.time);
                break;
            default:
                break;
        }
    };

    private startTrack = (
        track: CreatorSessionTrack,
        startTime: number,
    ): void => {
        const dyadicBarsLengthInSeconds = getDyadicBarsLengthInSeconds(
            track.getScaledDurationInBars(this._baseBpm),
            this._baseBpm,
        );

        const stopTime = startTime + dyadicBarsLengthInSeconds;
        const nextStartTime = stopTime;

        if (this._useLegacyProcessing) {
            const bufferNode = this.audioContext.createBufferSource();

            bufferNode.loop = false;
            bufferNode.buffer = track.warpBuffer;
            bufferNode.connect(track.gainNode);
            bufferNode.start(startTime);
            bufferNode.stop(stopTime);

            if (track.activeBufferNode) {
                track.staleBufferNode = track.activeBufferNode;
            }

            track.activeBufferNode = bufferNode;
        } else {
            track.startAt(startTime);
            track.stopAt(stopTime);
        }

        this.eventQueue.add({
            time: nextStartTime,
            trackId: track.id,
            action: CreatorPlaybackAction.START,
        });

        const startTimeInMS =
            (startTime - this.audioContext.currentTime) * 1000;

        // For updating the waveform
        setTimeout(() => {
            track.loopStartedAt = startTime;
            this.broadcast(CreatorEvents.TRACK_STARTED);
        }, startTimeInMS);
    };

    /**
     * TRACK CONTROLS
     */

    public updateTrackGain = (guid: string, gain: number): void => {
        this.getTrackById(guid).updateGain(gain);
    };

    public updateTrackBpm = (id: string, bpm: number): void => {
        this.processing();

        const track = this.getTrackById(id);

        setTimeout(() => {
            track.updateBpm({ baseBpm: this._baseBpm, bpm });
            this.broadcast(CreatorEvents.TRACKS_UPDATED);
        }, this.timeoutInMs);
    };

    public updateTrackBpmMultiplier = (
        id: string,
        bpmMultiplier: CreatorBpmMultiplier,
    ): void => {
        this.processing();
        this.stopPlaying();

        setTimeout(() => {
            this.getTrackById(id).updateBpm({
                baseBpm: this._baseBpm,
                bpmMultiplier: bpmMultiplier,
            });
            this.broadcast(CreatorEvents.TRACKS_UPDATED);
        }, this.timeoutInMs);
    };

    public updateTrackTranspose = (
        id: string,
        pitchTranspose: number,
    ): void => {
        this.processing();
        this.stopPlaying();

        setTimeout(() => {
            this.getTrackById(id).updatePitch({
                pitchTranspose,
            });
            this.broadcast(CreatorEvents.TRACKS_UPDATED);
        }, this.timeoutInMs);
    };

    /**
     * SESSION PROCESSING
     */

    private processing = (): void => {
        this.stopPlaying();

        if (this._tracks.size) {
            this.isProcessing = true;
            this.broadcast(CreatorEvents.PROCESSING_UPDATED);
        }
    };

    private stopProcessing = (): void => {
        this.isProcessing = false;
        this.broadcast(CreatorEvents.PROCESSING_UPDATED);
    };

    /**
     * SESSION CONTROLS
     */

    // PLAY / PAUSE

    public pause = (): void => {
        this.isPaused = true;
        this.broadcast(CreatorEvents.PAUSE_UPDATED);
        this.stopPlaying();
    };

    public unPause = (): void => {
        if (this.isProcessing) {
            return;
        }

        this.isPaused = false;
        this.broadcast(CreatorEvents.PAUSE_UPDATED);
        this.startPlaying();
    };

    public togglePause = (): void => {
        if (this._isPlaying) {
            this.pause();
            return;
        }

        this.unPause();
    };

    private setMasterVolume = (volume: number): void => {
        this.masterGainNode.gain.setValueAtTime(
            volume,
            this.audioContext.currentTime,
        );
    };

    private startPlaying = (): void => {
        this.resumeContext();

        this.stopProcessing();

        if (!this._tracks.size || this.isPaused) {
            return;
        }

        this.resetTracksStartedAt();

        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        if (!this._isPlaying) {
            this.setMasterVolume(1);

            this._isPlaying = true;

            this.rescheduleAllTracks();
        }
    };

    private stopPlaying = (): void => {
        this.resetTracksStartedAt();

        if (this._isPlaying) {
            this.setMasterVolume(0);

            this._isPlaying = false;

            this.stopTrackScheduler();
        }
    };

    // BASE

    public setBaseBpm = (bpm: number): void => {
        this.processing();

        // The `setTimeout` is an unfortunate hack so that processing state
        // activates correctly in Safari
        setTimeout(() => {
            this._baseBpm = bpm;

            this._tracks.forEach((track) => {
                track.updateBpm({ baseBpm: this._baseBpm });
            });

            this.broadcast(CreatorEvents.TRACKS_UPDATED);
        }, this.timeoutInMs);
    };

    public setBaseKey = (
        root: KeyRoot | KeyRootAPI,
        quality: KeyQuality,
    ): void => {
        this.processing();

        // The `setTimeout` is an unfortunate hack so that processing state
        // activates correctly in Safari
        setTimeout(() => {
            const keyAsSemitone = getKeyAsSemitone(root, quality);

            this.baseKeyRoot = root;
            this.baseKeyQuality = quality;
            this.baseKeyAsSemitone = keyAsSemitone ? keyAsSemitone : 0;

            this._tracks.forEach((track) => {
                track.updatePitch({
                    baseKeyAsSemitone: this.baseKeyAsSemitone,
                });
            });

            this.broadcast(CreatorEvents.TRACKS_UPDATED);
        }, this.timeoutInMs);
    };

    private promoteTrack = (track: CreatorSessionTrack): void => {
        this.baseTrackId = track.id;
        this.broadcast(CreatorEvents.PLAYBACK_UPDATED);
    };

    private promoteLongestTrack = (): void => {
        this.promoteTrack(this._longestTrack);
    };

    private resumeContext = (): void => {
        if (this.audioContext.state === 'suspended') {
            try {
                this.audioContext.resume();
            } catch (error) {
                log.error(
                    `Failed To Resume AudioContext`,
                    EventIds.FailedToResumeAudioContext,
                    error,
                );
            }
        }
    };

    // Unfortunately Safari's implementation is inconsistent with
    // the API spec, so occasionally resumeContext will have to be
    // invoked very low on the call chain. Otherwise, this method
    // should not be needed.
    public __resumeContext = this.resumeContext;

    /**
     * TRACK MANAGEMENT
     */

    public addTracks = async (
        tracks: CreatorTrackConfigType[],
    ): Promise<void> => {
        this.resumeContext();

        this.stopPlaying();

        // Async implementation of `foreach`
        await this.initTracks(tracks);

        this.broadcast(CreatorEvents.TRACKS_UPDATED);
    };

    private initTracks = async (
        tracks: CreatorTrackConfigType[],
    ): Promise<void> => {
        const promises = [];

        for (let index = 0; index < tracks.length; index++) {
            promises.push(
                this.initializeTrack(tracks[index].id, tracks[index]),
            );
        }

        await Promise.all(promises);
    };

    private initializeTrack = async (
        id: string,
        config: CreatorTrackConfigType,
    ): Promise<void> => {
        const track = new CreatorSessionTrack({
            id,
            config,
            session: this,
            onTrackReady: this.handleTrackReady,
            useLegacyProcessing: this._useLegacyProcessing,
        });

        this._tracks.set(id, track);

        this.processing();
        // the setTimeout is an unfortunate hack so that processing state
        // activates correctly in Safari
        setTimeout(async () => {
            try {
                await track.init({
                    baseKeyAsSemitone: this.baseKeyAsSemitone,
                    baseBpm: this._baseBpm,
                });
            } catch (error) {
                log.error(
                    `Failed To Init Track`,
                    EventIds.FailedToInitTrack,
                    error,
                );
                this.removeTrack(id, false);
            }
        }, this.timeoutInMs);
    };

    public removeAllTracks = (): void => {
        this._tracks.forEach((_, id) => {
            this.removeTrack(id, false);
        });
        this.stopProcessing();
    };

    public removeTrack = (id: string, resetGainOnAllTracks = true): void => {
        const track = this.getTrackById(id);
        const resumePlaying = this._isPlaying;

        this.stopPlaying();

        track.remove();

        this._tracks.delete(track.id);

        // handleTrackReady to trigger stopProcessing if all remaining tracks ready
        this.handleTrackReady(resetGainOnAllTracks);

        // Pause when we have no tracks left
        if (!this._tracks.size) {
            this.pause();
        } else if (resumePlaying) {
            setTimeout(this.startPlaying, this.timeoutInMs);
        }

        this.broadcast(CreatorEvents.TRACKS_UPDATED);
    };

    private setGainScaleOnAllTracks = (): void => {
        const { size } = this._tracks;
        const ratio = PlaybackTargetAmplitude / size;

        this._tracks.forEach((track) => {
            const { peakAmplitude } = track;

            // Don't accidentally divide by 0
            const gainScale = ratio / (peakAmplitude || 1);

            track.updateGainScale(gainScale);
        });
    };

    /**
     * MIXDOWN
     */

    private async createMixdownWorker(
        longestTrackLengthInSamples: number,
        minLengthInSamples: number,
        maxLengthInSamples: number,
        sampleRate: number,
        tracks: CreatorMixdownTrackType[],
        doDownload: boolean,
    ) {
        this.mixdownWorker = new window.Worker(
            '/workers/creatorMixdownWorker.js',
        );

        this.mixdownWorker.onmessage = (e) => {
            const { mp3Blob, importError } = e.data;

            if (importError) {
                log.error(
                    'Fail to import lame.min.js',
                    EventIds.FailToImportLameJs,
                );

                // If we get an error, wait two seconds then try again
                if (this.mixdownRetries < MaxMixdownRetries) {
                    this.mixdownRetries += 1;

                    this.mixdownWorker?.terminate();

                    setTimeout(
                        () =>
                            this.createMixdownWorker(
                                longestTrackLengthInSamples,
                                minLengthInSamples,
                                maxLengthInSamples,
                                sampleRate,
                                tracks,
                                doDownload,
                            ),
                        2000,
                    );
                }

                return;
            }

            this.mixdownWorker?.terminate();

            if (doDownload) {
                downloadMixdown(
                    mp3Blob,
                    this.baseKeyRoot,
                    this.baseKeyQuality,
                    this.baseBpm,
                );
            }

            this.resolveMixdown?.(mp3Blob);
        };

        this.mixdownWorker.postMessage({
            longestTrackLengthInSamples,
            maxGainPeak: MixdownGainPeak,
            minLengthInSamples,
            maxLengthInSamples,
            mp3BitRate: MixdownMp3BitRate,
            sampleRate,
            tracks,
        });
    }

    public generateMixdown = (
        minDurationInSeconds: number,
        maxDurationInSeconds: number,
        download = false,
    ): Promise<Blob> => {
        this.mixdownWorker?.terminate();

        const getMixdownPromise = () =>
            new Promise<Blob>((resolve) => {
                const { sampleRate } = this.audioContext;

                const maxLengthInSamples = getSampleCountFromSeconds(
                    maxDurationInSeconds,
                    sampleRate,
                );

                const minLengthInSamples = getSampleCountFromSeconds(
                    minDurationInSeconds,
                    sampleRate,
                );

                const longestTrackLengthInSamples =
                    this._longestTrack.getScaledDyadicLengthInSamples(
                        this._baseBpm,
                        sampleRate,
                    );

                const tracks = [...this.tracks.values()].map((track) => {
                    const dyadicLengthInSamples =
                        track.getScaledDyadicLengthInSamples(
                            this._baseBpm,
                            sampleRate,
                        );

                    const gain = track.calculatedGain;
                    const channelL = track.warpBuffer.getChannelData(0);
                    const channelR = track.warpBuffer.getChannelData(1);

                    return {
                        dyadicLengthInSamples,
                        gain,
                        channelL,
                        channelR,
                    };
                });

                this.createMixdownWorker(
                    longestTrackLengthInSamples,
                    minLengthInSamples,
                    maxLengthInSamples,
                    sampleRate,
                    tracks,
                    download,
                );

                this.resolveMixdown = (value: Blob | PromiseLike<Blob>) => {
                    resolve(value);
                    this.resolveMixdown = undefined;
                };
            });

        if (this.isInitialized && !this.isProcessing) {
            return getMixdownPromise();
        }

        const mixdownWaitingForProcessingPromise = new Promise<Blob>(
            (resolve) => {
                this.resolveMixdownWaitingForProcessing = () => {
                    resolve(getMixdownPromise());

                    this.resolveMixdownWaitingForProcessing = undefined;
                };
            },
        );

        return mixdownWaitingForProcessingPromise;
    };

    public cancelMixdown = (): void => {
        this.mixdownWorker?.terminate();
    };
}
